import { useEffect } from "react";

import { useInfiniteQuery } from "react-query";

import config from "@/config";
import { CommonSearchRequestParams, FeatureItem, FeaturePagination } from "@/shared/domain";

export type useSearchFromEndpointProps<ObjectType> = {
    result: ObjectType[];
    isFetching: boolean;
};

const useSearchFromEndpoint = <
    ObjectType extends FeatureItem,
    ObjectPaginationType extends FeaturePagination<ObjectType>,
    SearchParamsType extends CommonSearchRequestParams,
>(
    cacheKey: string,
    endPoint: (request: SearchParamsType) => Promise<ObjectPaginationType>,
    policyholdersIdList,
    filterItems: (items: ObjectType[]) => ObjectType[],
    otherParams: Partial<SearchParamsType> = {},
): useSearchFromEndpointProps<ObjectType> => {
    const pageSize = config.DEFAULT_SEARCH_PAGE_SIZE;

    const lastPageHasMaxItems = (pages: ObjectPaginationType[]) =>
        !pages || (pages.length > 0 && pages[pages.length - 1].result.length === pageSize);

    const { data, isFetching, fetchNextPage } = useInfiniteQuery(
        [`${cacheKey}Full`, { ...otherParams }],
        ({ pageParam = 1 }) => {
            return endPoint({
                pageNumber: pageParam,
                pageSize,
                policyholdersIdList,
                ...otherParams,
            } as SearchParamsType);
        },
        {
            suspense: false,
            refetchOnWindowFocus: false,
            getNextPageParam: (lastFetch) => {
                return lastFetch.pageNumber + 1;
            },
        },
    );

    useEffect(() => {
        // last received page has the max items >> do another call
        if (!!data && lastPageHasMaxItems(data.pages)) {
            fetchNextPage();
        }
    }, [data]);

    return {
        result: data !== undefined ? filterItems(data.pages?.map((d) => d.result)?.flat()) : [],
        isFetching,
    };
};

export default useSearchFromEndpoint;
